import { useState } from 'react'
import { useForm } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { CalendarQuarter } from '@klear/date/calendar-quarter'
import type { TraderDocument } from '@klear/document/trader-document'
import type { FinancialPeriodEnd } from '@klear/schema/documents'
import {
  documentFormFieldErrors,
  hasDocumentFormErrors,
  validateDocumentForm,
  type DocumentForm
} from '@klear/document/wire.client'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@klear/ui/components/dialog'
import { createToastConfig, useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { trpc } from '@klear/ui/lib/trpc'
import TraderDocumentForm from '@/app/document/document-form-common'

function periodEndDefaults(periodEnd: CalendarQuarter | null) {
  if (periodEnd === null) return { financialStatement: false }
  return {
    financialStatement: true,
    periodEndYear: toString(periodEnd.year),
    periodEndQuarter: toString(periodEnd.quarter)
  }
}

function periodEndOf(data: DocumentForm): FinancialPeriodEnd | null {
  if (data.financialStatement !== true) return null
  return CalendarQuarter.of({
    year: toNumber(data.periodEndYear),
    quarter: toNumber(data.periodEndQuarter)
  }).toPeriodEnd()
}

type DocumentEdits = { name?: string; notes?: string | null; periodEnd?: FinancialPeriodEnd | null }

function editsOf(document: TraderDocument, data: DocumentForm): DocumentEdits {
  const edits: DocumentEdits = {}
  const name = data.title.trim()
  if (name !== document.name) edits.name = name
  const notes = (data.notes ?? '').trim()
  if (notes !== document.notes.trim()) edits.notes = notes === '' ? null : notes
  const periodEnd = periodEndOf(data)
  if (!isEqual(periodEnd, document.periodEnd?.toJSON() ?? null)) edits.periodEnd = periodEnd
  return edits
}

type EditDocumentDialogProps = {
  document: TraderDocument
  deleteBlockedReason: string | null
  onClose: () => void
  onDelete?: () => void
}

export function EditDocumentDialog({ document, deleteBlockedReason, onClose, onDelete }: EditDocumentDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user } = useAuth()
  const updateDocument = useMutationWithToast(
    trpc.documents.update.useMutation,
    createToastConfig('Document', 'update'),
    {
      invalidateQueries: ['documents.list']
    }
  )

  const form = useForm<DocumentForm>({
    resolver: async (values) => {
      const errors = validateDocumentForm(values, { requireFile: false })
      if (hasDocumentFormErrors(errors)) {
        return { values: {}, errors: documentFormFieldErrors(errors) }
      }
      return { values, errors: {} }
    },
    defaultValues: {
      id: document.id,
      title: document.name,
      notes: document.notes,
      ...periodEndDefaults(document.periodEnd)
    }
  })

  const onSubmit = async (data: DocumentForm) => {
    const edits = editsOf(document, data)
    if (isEmpty(edits)) {
      onClose()
      return
    }
    try {
      setIsSubmitting(true)
      await updateDocument.mutateAsync({ id: document.id, data: edits })
      onClose()
    } catch (err: unknown) {
      console.error('Error updating document:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Document</DialogTitle>
          <DialogDescription>
            Update the name, notes or financial period. To use a different file, upload a new document.
          </DialogDescription>
          {deleteBlockedReason !== null && <p className="text-sm text-muted-foreground">{deleteBlockedReason}</p>}
        </DialogHeader>
        <TraderDocumentForm
          form={form}
          onSubmit={onSubmit}
          onCancel={onClose}
          isEditMode
          currentUrl={document.name}
          isSubmitting={isSubmitting}
          onDelete={onDelete}
          deleteDisabled={deleteBlockedReason !== null && !user.isSuperAdmin}
          showFinancialToggle
        />
      </DialogContent>
    </Dialog>
  )
}
